import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';
import { toObservable } from '@angular/core/rxjs-interop';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { filter, map, takeUntil } from 'rxjs/operators';

import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { MetaStore, RelationsStore } from '@qtek/libs/store';
import { Customer, PhoneType, Staff } from '@qtek/shared/models';
import { Observable, Subject } from 'rxjs';

export const contactPreviewDialogConfig: MatDialogConfig = {
  panelClass: 'fullscreen',
  autoFocus: false,
};

export interface ContactPrevievDialogInput extends Staff {
  type: 'staff' | 'customer';
}

@Component({
  selector: 'qt-contact-preview-dialog',
  templateUrl: './contact-preview-dialog.component.html',
  styleUrls: ['./contact-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContactPreviewDialogComponent implements OnDestroy {
  private readonly data = inject<ContactPrevievDialogInput>(MAT_DIALOG_DATA);
  protected readonly media = inject(MediaObserver);
  private metaStore = inject(MetaStore);
  private relationsStore = inject(RelationsStore);
  private dialogLoader = inject(LazyDialogLoader);

  contact = signal<Customer | Staff | null>(null);

  type: 'staff' | 'customer';
  destroy$ = new Subject<void>();

  protected readonly role = computed(() => {
    const r = this.metaStore
      .allRoles()
      .find(role => role.id === this.data.rl?.toString());
    return r ? r.name : '';
  });

  get relation(): string {
    switch (this.type) {
      case 'customer':
        return 'PTL_GRP_CUSTOMER';
      case 'staff':
        return 'GNR_STAFF';
      default:
        return '';
    }
  }

  constructor() {
    this.type = this.data.type;

    const relations$: Observable<Staff[] | Customer[]> =
      this.type === 'staff'
        ? toObservable(this.relationsStore.staff)
        : toObservable(this.relationsStore.customers);

    relations$
      .pipe(
        map(relations =>
          this.type === 'staff'
            ? relations.find((staff: Staff) => staff.prsId === this.data.prsId)
            : relations.find(
                (customer: Customer) => customer.relId === this.data.relId
              )
        ),
        takeUntil(this.destroy$),
        filter(Boolean)
      )
      .subscribe(contact => {
        this.contact.set(contact);
      });
  }

  protected readonly PhoneType = PhoneType;

  onEdit(): void {
    if (this.type === 'staff') {
      this.dialogLoader.open('employee-dialog', this.contact()).subscribe();
    } else {
      this.dialogLoader
        .open('customer-dialog', { customer: this.contact() })
        .subscribe();
    }
  }

  onInvite(): void {
    if (this.type === 'staff') {
      this.dialogLoader
        .open('invite-contact', {
          type: 'staff',
          relId: btoa(this.contact()._id),
          person: this.contact().prs,
        })
        .subscribe();
    } else {
      this.dialogLoader
        .open('invite-contact', {
          type: 'customer',
          relId: this.contact().relId,
          person: this.contact().prs,
          customer: this.contact(),
        })
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
